import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import api from "../lib/api";

/**
 * AudioPlayer — fetches the audio blob (auth-required) and plays it.
 * Usage: <AudioPlayer path={story.audio_path} />
 */
export const AudioPlayer = ({ path, label = "Hear their voice." }) => {
  const [url, setUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  const fetchAudio = async () => {
    if (url || loading) return;
    setLoading(true);
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      setUrl(u);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const toggle = async () => {
    if (!url) await fetchAudio();
    setTimeout(() => {
      const el = audioRef.current;
      if (!el) return;
      if (el.paused) { el.play(); setPlaying(true); }
      else { el.pause(); setPlaying(false); }
    }, 50);
  };

  if (!path) return null;

  return (
    <div className="flex items-center gap-4 px-5 py-4 hairline-cream bg-[#F7F0E0]" style={{ borderRadius: 4 }} data-testid="audio-player">
      <button
        onClick={toggle}
        className="flex items-center justify-center transition-colors duration-500 hairline"
        style={{ width: 48, height: 48, borderRadius: 999, background: "#B8973A", color: "#FAF7F2", borderColor: "#B8973A" }}
        aria-label={playing ? "Pause" : "Play"}
        data-testid="audio-play-btn"
      >
        {loading ? <span className="text-xs">…</span> : playing ? <Pause size={18} /> : <Play size={18} />}
      </button>
      <div className="flex-1">
        <p className="label-ui text-[10px] text-[#B8973A]">Voice recording</p>
        <p className="italic text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
          {label}
        </p>
      </div>
      {url && (
        <audio ref={audioRef} src={url} onEnded={() => setPlaying(false)} className="hidden" data-testid="audio-element" />
      )}
    </div>
  );
};

export default AudioPlayer;
